import { waypointMarkerFactory } from 'common/utils/waypointMarker';
import getGUID from 'common/utils/getGUID';
import pako from 'pako';

import { intl } from 'i18n';

import { Map } from "common/utils/Map";
import { WaypointMarker } from 'common/utils/waypointMarker/types/waypointMarker';
import {
  Params,
  Route,
  Fn as AddListenerClickFn
} from '../types';

import InfoWindow from './InfoWindow';

const MIN_POINT_COUNT = 300;

const computePathPoints = (path: number, lengthKm: number): number => {
  const byDensity  = 2 * Math.sqrt(path);
  const byDistance = 10 * Math.sqrt(lengthKm);

  return Math.max(MIN_POINT_COUNT, Math.min(byDensity, byDistance));
}
export class GoogleRoute implements Route {
  private _map: Map;
  private _waypointMarkers: WaypointMarker[] = [];
  private _id: string;
  private _infoWindow?: InfoWindow;
  private _polyline?: google.maps.Polyline;
  private _polylineOptions = {
    strokeColor: '#008DC9',
    strokeWeight: 5,
    zIndex: 0,
    clickable: false
  };
  public routePoints: Params['routePoints'];
  public time: Params['time'];
  public startAddress: Params['startAddress'];
  public endAddress: Params['endAddress'];
  public distance: Params['distance'];
  public bounds: Params['bounds'];
  public legs: Params['legs'];
  public path: Params['path'];
  public shortPath: Route['shortPath'] = [];
  public duration: Route['duration'] = 0;
  isFastest: Route['isFastest'] = false;
  isCheapest: Route['isCheapest'] = false;

  constructor(map: Map, params: Params) {
    const {
      time,
      startAddress,
      endAddress,
      distance,
      bounds,
      legs,
      routePoints,
      path
    } = params;

    this._map = map;
    this._id = getGUID();
    this.time = time;
    this.startAddress = startAddress;
    this.endAddress = endAddress;
    this.distance = distance;
    this.bounds = bounds;
    this.legs = legs;
    this.routePoints = routePoints;
    this.path = path;
    this._initShortPath();
    this._createPolyline();
  }

  public get id() {
    return this._id;
  }

  _initShortPath() {
    if (this.path) {
      if (this.path.length <= MIN_POINT_COUNT) {
        this.shortPath = this.path;
      } else {
        const pathPoints = computePathPoints(this.path.length, Number(this.distance));
        const shift = Math.floor(this.path.length / pathPoints);
        this.shortPath = this.path.reduce((acc, item, index) => {
          if (
            index === 0 ||
            index % shift === 0 ||
            index === (this.path || []).length - 1
          ) {
            acc.push(item);
          }
          
          return acc;
        }, [] as { lat: number; lng: number; }[]);
      }
    }
  }

  _createPolyline(
    map?: google.maps.Map,
    polylineOptions?: google.maps.PolylineOptions
  ) {
    this._polyline = new google.maps.Polyline({
      map,
      path: this.path,
      ...this._polylineOptions,
      ...polylineOptions
    });
  }

  _createWaypointMarkers() {
    if (this.routePoints) {
      const routePoints = [
        this.routePoints.firstPoint,
        ...this.routePoints.waypoints,
        this.routePoints.lastPoint
      ];

      this._waypointMarkers = routePoints.map(({ coordinates }, index) => {
        let name = '';
        
        if (index === 0) {
          name = 'startPoint';
        } else if (index === routePoints.length - 1) {
          name = 'endPoint';
        } else {
          name = `waypoint${index}`;
        }

        return waypointMarkerFactory(
          this._map,
          // @ts-ignore
          { location: { lat: coordinates?.lat, lng: coordinates?.lng }, name }
        )
      });
    }
  }

  _deleteWaypointMarkers() {
    this._waypointMarkers.forEach(waypointMarker => {
      waypointMarker.hide();
    });
    this._waypointMarkers = [];
  }

  _showRoute() {
    if (this._polyline && this._map) {
      this._polyline.setMap(this._map.getNativeMap());
    }
  }

  _deleteRoute() {
    if (this._polyline) {
      this._polyline.setMap(null);
    }
  }

  addInfoWindow() {
    if (this.path) {
      this._infoWindow = new InfoWindow(
        this._map,
        {
          overviewPath: this.path,
          distance: `${this.distance} ${intl.formatMessage({ id: 'km' })}`,
          time: this.time
        }
      );
      this._infoWindow.add();
    }
  }


  deleteInfoWindow() {
    if (this._infoWindow) this._infoWindow.delete();
  }

  selectRoute(isSelected: boolean) {
    this._deleteRoute();

    const polylineOptions = {
      strokeColor: isSelected ? '#4FBC46' : this._polylineOptions.strokeColor,
      zIndex: isSelected ? 5 : 0,
    };

    this._createPolyline(this._map.getNativeMap(), polylineOptions);
  }

  show() {
    this._showRoute();
    this._createWaypointMarkers();
  }

  delete() {
    this._deleteRoute();
    this._deleteWaypointMarkers();
    this.deleteInfoWindow();
  }

  addListenerClick(fn: AddListenerClickFn) { }

  getReportRouteSettings() {
    const coordinates = (this.path || []).reduce((acc, item) => {
      acc += `${item.lat},${item.lng};`
      return acc;
    }, '');
    const bytes = pako.deflate(coordinates);
    const decodedPath = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');

    return ({
      time: this.time,
      distance: this.distance,
      bounds: this.bounds,
      legs: this.legs,
      routePoints: this.routePoints,
      decodedPath
    });
  }
}
